import axios from 'axios'
import { bffBaseURL, normalizeError } from './apiClient'
import type { components } from '@/types/generated'

// ============================================================================
// Skills API — Phase 57
// ============================================================================

export async function searchSkills(
  query: string = '',
  options: Record<string, any> = {}
): Promise<components['schemas']['SkillSearchResponse']> {
  try {
    const params = new URLSearchParams({ q: query, ...options })
    const response = await axios.get(
      `${bffBaseURL}/api/v1/skills?${params.toString()}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Skills search failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function getSkillCategories() {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/skills/categories`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get categories')
      throw new Error(message)
    }
    throw error
  }
}

export async function getSkill(skillId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/skills/${skillId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get skill')
      throw new Error(message)
    }
    throw error
  }
}

export async function getSkillsByCategory(category: string, params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/skills/by-category/${encodeURIComponent(category)}${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get skills by category')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Skill Creation — Phase 74
// ============================================================================

export interface SkillCreateInput {
  canonical_name: string
  display_name: string
  category: string
  display_name_en?: string
  aliases?: string[]
}

export interface SkillCreateResponse {
  id: string
  canonical_name: string
  display_name: string
  display_name_en: string | null
  category: string
  source: string
  esco_uri: string | null
  aliases: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export async function createSkill(data: SkillCreateInput): Promise<SkillCreateResponse> {
  try {
    const response = await axios.post<SkillCreateResponse>(
      `${bffBaseURL}/api/v1/skills`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = (error.response?.data as { detail?: string })?.detail
      const httpStatus = error.response?.status
      const message = normalizeError(detail || 'Failed to create skill')
      const err = new Error(message) as Error & { httpStatus?: number }
      err.httpStatus = httpStatus
      throw err
    }
    throw error
  }
}
